import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAdminUserDetail, type UserDetail } from "../../hooks/useAdminUsers";
import { ActionPanel } from "../../components/admin/ActionPanel";

/**
 * UserDetailPage - Admin page to view individual user details.
 *
 * Displays:
 * - User account info
 * - Subscription details with Stripe link
 * - Children profiles with progress
 * - Referral information
 */
export function UserDetailPage() {
  const { profileId } = useParams<{ profileId: string }>();
  const { user, isLoading, error, refetch } = useAdminUserDetail(profileId);
  const [activeTab, setActiveTab] = useState<
    "account" | "children" | "subscription" | "referrals" | "actions"
  >("account");

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        <BackLink />
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-red-800 mb-2">
            Error Loading User
          </h2>
          <p className="text-sm text-red-600">{error || "User not found"}</p>
        </div>
      </div>
    );
  }

  const displayName =
    user.profile.name ||
    user.profile.email?.split("@")[0] ||
    "Unknown User";

  return (
    <div className="space-y-6">
      {/* Back link */}
      <BackLink />

      {/* User header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-magic-100 flex items-center justify-center flex-shrink-0">
              <span className="text-magic-600 font-bold text-2xl">
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-gray-500">
                  {user.profile.email}
                </span>
                {user.profile.emailVerified ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                    <CheckIcon className="w-3 h-3 mr-1" />
                    Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                    Unverified
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 font-mono mt-1">
                ID: {user.profile.id}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {user.profile.role === "admin" && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                Admin
              </span>
            )}
            <TierBadge
              tier={user.subscription?.tier || "free"}
              status={user.subscription?.status}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {(
            [
              { id: "account", label: "Account" },
              { id: "children", label: `Children (${user.children.length})` },
              { id: "subscription", label: "Subscription" },
              { id: "referrals", label: "Referrals" },
              { id: "actions", label: "Actions" },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-3 px-1 text-sm font-medium border-b-2 transition-colors
                ${
                  activeTab === tab.id
                    ? "border-magic-500 text-magic-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="min-h-[400px]">
        {activeTab === "account" && <AccountTab user={user} />}
        {activeTab === "children" && <ChildrenTab user={user} />}
        {activeTab === "subscription" && <SubscriptionTab user={user} />}
        {activeTab === "referrals" && <ReferralsTab user={user} />}
        {activeTab === "actions" && (
          <ActionPanel user={user} onActionComplete={refetch} />
        )}
      </div>
    </div>
  );
}

// ----- Tab Components -----

function AccountTab({ user }: { user: UserDetail }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Account Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Account Details
        </h2>
        <div className="space-y-4">
          <InfoRow label="Email" value={user.profile.email || "N/A"} />
          <InfoRow
            label="Email Status"
            value={user.profile.emailVerified ? "Verified" : "Unverified"}
            valueClassName={
              user.profile.emailVerified ? "text-green-600" : "text-yellow-600"
            }
          />
          <InfoRow
            label="Created"
            value={formatDateTime(user.profile.createdAt)}
          />
          <InfoRow
            label="Last Login"
            value={
              user.profile.lastLoginAt
                ? formatRelativeTime(user.profile.lastLoginAt)
                : "Never"
            }
          />
          <InfoRow label="Auth Provider" value={capitalizeFirst(user.profile.provider)} />
          <InfoRow label="Role" value={capitalizeFirst(user.profile.role)} />
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h2>
        <div className="grid grid-cols-2 gap-4">
          <StatCard
            label="Children"
            value={user.children.length}
            sublabel={`of ${user.subscription?.childLimit || 1} allowed`}
          />
          <StatCard
            label="Total Stars"
            value={user.children.reduce(
              (sum, c) => sum + c.progressSummary.totalStars,
              0
            )}
            sublabel="across all children"
          />
          <StatCard
            label="Badges"
            value={user.children.reduce(
              (sum, c) => sum + c.progressSummary.totalBadges,
              0
            )}
            sublabel="earned"
          />
          <StatCard
            label="Referrals"
            value={user.referral.referralCount}
            sublabel="successful"
          />
        </div>
      </div>

      {/* Email History Link */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
              <EmailIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Email History</h3>
              <p className="text-sm text-gray-500">View sent emails, sequences, and preferences</p>
            </div>
          </div>
          <Link
            to={`/admin/users/${user.profile.id}/emails`}
            className="
              px-4 py-2 text-sm font-medium rounded-lg
              bg-purple-50 text-purple-700 hover:bg-purple-100
              transition-colors inline-flex items-center gap-2
            "
          >
            View Emails
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function ChildrenTab({ user }: { user: UserDetail }) {
  if (user.children.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
        <div className="text-center">
          <ChildIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Children Profiles
          </h3>
          <p className="text-sm text-gray-500">
            This user hasn't created any child profiles yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {user.children.map((child) => (
        <div
          key={child.id}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-sunshine-100 flex items-center justify-center">
                <span className="text-sunshine-600 font-bold text-lg">
                  {child.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {child.name}
                </h3>
                <p className="text-sm text-gray-500">
                  Last active:{" "}
                  {child.lastActiveAt
                    ? formatRelativeTime(child.lastActiveAt)
                    : "Never"}
                </p>
              </div>
            </div>
            <Link
              to={`/admin/children/${child.id}`}
              className="text-sm font-medium text-magic-600 hover:text-magic-700"
            >
              View Progress
            </Link>
          </div>

          {/* Progress summary */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Stars</p>
              <p className="text-xl font-bold text-gray-900">
                {child.progressSummary.totalStars}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Badges</p>
              <p className="text-xl font-bold text-gray-900">
                {child.progressSummary.totalBadges}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 col-span-2">
              <p className="text-xs text-gray-500 mb-1">Discipline Levels</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(child.progressSummary.currentLevels).length >
                0 ? (
                  Object.entries(child.progressSummary.currentLevels).map(
                    ([discipline, level]) => (
                      <span
                        key={discipline}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-magic-100 text-magic-700"
                      >
                        {capitalizeFirst(discipline)}: L{level}
                      </span>
                    )
                  )
                ) : (
                  <span className="text-sm text-gray-400">No progress yet</span>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SubscriptionTab({ user }: { user: UserDetail }) {
  const sub = user.subscription;

  if (!sub) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <CreditCardIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Subscription
          </h3>
          <p className="text-sm text-gray-500">
            This user does not have an active subscription record.
          </p>
        </div>
      </div>
    );
  }

  const stripeDashboardUrl = sub.stripeCustomerId
    ? `https://dashboard.stripe.com/customers/${sub.stripeCustomerId}`
    : null;

  return (
    <div className="space-y-6">
      {/* Subscription Overview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Subscription Details
          </h2>
          {stripeDashboardUrl && (
            <a
              href={stripeDashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[#635BFF] rounded-lg hover:bg-[#5148E5] transition-colors"
            >
              <StripeIcon className="w-4 h-4" />
              View in Stripe
            </a>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <InfoRow label="Tier" value={capitalizeFirst(sub.tier)} />
            <InfoRow
              label="Status"
              value={capitalizeFirst(sub.status)}
              valueClassName={getStatusColor(sub.status)}
            />
            <InfoRow label="Child Limit" value={String(sub.childLimit)} />
            {sub.cancelAtPeriodEnd && (
              <div className="px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium">
                  Cancels at period end
                </p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <InfoRow
              label="Current Period Start"
              value={
                sub.currentPeriodStart
                  ? formatDateTime(sub.currentPeriodStart)
                  : "N/A"
              }
            />
            <InfoRow
              label="Current Period End"
              value={
                sub.currentPeriodEnd
                  ? formatDateTime(sub.currentPeriodEnd)
                  : "N/A"
              }
            />
            <InfoRow
              label="Started"
              value={formatDateTime(sub.createdAt)}
            />
          </div>
        </div>
      </div>

      {/* Stripe IDs */}
      {(sub.stripeCustomerId || sub.stripeSubscriptionId) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Stripe Integration
          </h2>
          <div className="space-y-3">
            {sub.stripeCustomerId && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Customer ID</span>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                  {sub.stripeCustomerId}
                </code>
              </div>
            )}
            {sub.stripeSubscriptionId && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Subscription ID</span>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                  {sub.stripeSubscriptionId}
                </code>
              </div>
            )}
            {sub.stripePriceId && (
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-500">Price ID</span>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                  {sub.stripePriceId}
                </code>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReferralsTab({ user }: { user: UserDetail }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Referral Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Referral Activity
        </h2>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <StatCard
            label="Own Code"
            value={user.referral.ownCode || "None"}
            isCode
          />
          <StatCard
            label="Successful Referrals"
            value={user.referral.referralCount}
          />
        </div>
      </div>

      {/* Referred By */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Referred By
        </h2>
        {user.referral.referredBy ? (
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 rounded-full bg-magic-100 flex items-center justify-center">
              <span className="text-magic-600 font-semibold text-sm">
                {user.referral.referredBy.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {user.referral.referredBy.name}
              </p>
              <p className="text-xs text-gray-500">
                {user.referral.referredBy.email}
              </p>
              {user.referral.usedCode && (
                <p className="text-xs text-gray-400 mt-1">
                  Code: <code className="bg-gray-200 px-1 rounded">{user.referral.usedCode}</code>
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">This user was not referred by anyone.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ----- Helper Components -----

function BackLink() {
  return (
    <Link
      to="/admin/users"
      className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
    >
      <ArrowLeftIcon className="w-4 h-4" />
      Back to Users
    </Link>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-32" />
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-gray-200" />
          <div className="flex-1">
            <div className="h-8 bg-gray-200 rounded w-48 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-64 mb-1" />
            <div className="h-3 bg-gray-100 rounded w-96" />
          </div>
        </div>
      </div>
      <div className="h-10 bg-gray-200 rounded w-full" />
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-100 rounded w-full" />
            <div className="h-4 bg-gray-100 rounded w-full" />
            <div className="h-4 bg-gray-100 rounded w-full" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-20 bg-gray-100 rounded" />
            <div className="h-20 bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm text-gray-900 ${valueClassName || ""}`}>
        {value}
      </span>
    </div>
  );
}

function StatCard({
  label,
  value,
  sublabel,
  isCode,
}: {
  label: string;
  value: string | number;
  sublabel?: string;
  isCode?: boolean;
}) {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      {isCode ? (
        <code className="text-lg font-bold text-gray-900 bg-gray-200 px-2 py-0.5 rounded">
          {value}
        </code>
      ) : (
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      )}
      {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
    </div>
  );
}

function TierBadge({
  tier,
  status,
}: {
  tier: "free" | "family" | "classroom";
  status?: string;
}) {
  const tierStyles = {
    free: "bg-gray-100 text-gray-700",
    family: "bg-magic-100 text-magic-700",
    classroom: "bg-sunshine-100 text-sunshine-700",
  };

  const tierLabels = {
    free: "Free",
    family: "Family",
    classroom: "Classroom",
  };

  return (
    <div className="flex items-center gap-2">
      <span
        className={`
          inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
          ${tierStyles[tier]}
        `}
      >
        {tierLabels[tier]}
      </span>
      {status && status !== "active" && (
        <span
          className={`
            inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
            ${
              status === "canceled"
                ? "bg-red-100 text-red-700"
                : status === "past_due"
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-blue-100 text-blue-700"
            }
          `}
        >
          {status === "past_due" ? "Past Due" : capitalizeFirst(status)}
        </span>
      )}
    </div>
  );
}

// ----- Utility Functions -----

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getStatusColor(status: string): string {
  switch (status) {
    case "active":
      return "text-green-600";
    case "canceled":
      return "text-red-600";
    case "past_due":
      return "text-yellow-600";
    case "trialing":
      return "text-blue-600";
    default:
      return "text-gray-600";
  }
}

// ----- Icons -----

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 19l-7-7m0 0l7-7m-7 7h18"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

function ChildIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function CreditCardIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  );
}

function StripeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z" />
    </svg>
  );
}

function EmailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

export default UserDetailPage;
